import { generateId } from "@/utils/id";
import { z } from "zod";

// Base structure schemas
const SetsStructureSchema = z.object({
  type: z.literal("sets"),
  sets: z.number().int().positive(),
  reps: z.number().int().positive(),
});

const TimedStructureSchema = z.object({
  type: z.literal("timed"),
  sets: z.number().int().positive(),
  duration: z.number().int().positive(),
});

const CadenceStructureSchema = z.object({
  type: z.literal("cadence"),
  sets: z.number().int().positive(),
  reps: z.number().int().positive(),
  tempo: z.string(),
});

// Combined structure schema with discriminated union
export const ExerciseStructureSchema = z.discriminatedUnion("type", [
  SetsStructureSchema,
  TimedStructureSchema,
  CadenceStructureSchema,
]);

// Exercise schema
export const ExerciseSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(["strength", "cardio", "flexibility", "hiit", "other", "sets"]),
  structure: ExerciseStructureSchema,
  rest_time: z.number().int().nonnegative().optional().default(15),
  notes: z.string().optional(),
});

// Workout schema
export const WorkoutSchema = z.object({
  id: z.string(),
  name: z.string().default("New Workout"),
  description: z.string().nullable(),
  exercises: z.array(ExerciseSchema),
  user_id: z.string(),
});

// Exercise result schema for workout logs
export const ExerciseResultSchema = ExerciseSchema.extend({
  completed: z.boolean().default(false),
  sets_completed: z.number().int().default(0),
  actual_sets: z.number().int().optional(),
  actual_reps: z.number().int().optional(),
  actual_duration: z.number().int().optional(),
  notes: z.string().optional(),
});

// Workout snapshot schema
export const WorkoutSnapshotSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  description: z.string().nullish(),
  exercises: z.array(ExerciseSchema),
  exercise_results: z.record(z.string(), ExerciseResultSchema),
});

// Workout log schema
export const WorkoutLogSchema = z.object({
  id: z
    .string()
    .uuid()
    .default(() => crypto.randomUUID()),
  user_id: z.string().uuid(),
  workout_id: z.string(),
  workout_name: z.string(),
  status: z
    .enum(["in_progress", "completed", "cancelled"])
    .default("in_progress"),
  workout_snapshot: WorkoutSnapshotSchema.optional(),
  started_at: z.string().optional(),
  completed_at: z.string().nullable().optional(),
});

// Infer types from schemas
export type ExerciseStructure = z.infer<typeof ExerciseStructureSchema>;
export type Exercise = z.infer<typeof ExerciseSchema>;
export type ExerciseResult = z.infer<typeof ExerciseResultSchema>;
export type Workout = z.infer<typeof WorkoutSchema>;
export type WorkoutSnapshot = z.infer<typeof WorkoutSnapshotSchema>;
export type WorkoutLog = z.infer<typeof WorkoutLogSchema>;

export function createDefaultWorkout(userId: string): Workout {
  return WorkoutSchema.parse({
    id: generateId(),
    user_id: userId,
    name: "New Workout",
    description: "",
    type: "strength",
    structure: {
      type: "sets",
      sets: 3,
      reps: 10,
    },

    exercises: [],
  });
}

export function validateWorkout(workout: unknown): Workout {
  try {
    // Check if user_id is an object and convert it to string if needed
    if (workout && typeof workout === "object" && "user_id" in workout) {
      const workoutData = workout;

      // If user_id is an object with an id property, use that
      if (
        typeof workoutData.user_id === "object" &&
        workoutData.user_id !== null &&
        "id" in workoutData.user_id
      ) {
        workoutData.user_id = workoutData.user_id.id;
      }

      return WorkoutSchema.parse(workoutData);
    }

    return WorkoutSchema.parse(workout);
  } catch (error) {
    console.error("Workout validation error:", error);
    throw error;
  }
}
